import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { addToChainAfter, Handler, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { chain, ValidatePostalCode } from "@insite/client-framework/Store/CommonHandlers/ValidateAddress";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CustomerValidationDto,
    ValidatedAddressModel,
} from "@insite/client-framework/Types/ApiModels";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressValidationResult, AddressValidationApiParameter } from "@MasterBluePrint/Services/PiyoviService";
import { areValidatedAddressesEquivalent } from "@MasterBluePrint/Overrides/Common/Utilities/piyoviAddress";
import { piyoviSettingSelector } from "@MasterBluePrint/Store/Context/ContextSelector";
import { PiyoviAddressType } from "@MasterBluePrint/Store/Components/CustomCheckoutShipping/CustomCheckoutShippingState";
import { CustomApplicationState } from "@MasterBluePrint/Store/CustomApplicationState";


export type AddressErrors = SafeDictionary<React.ReactNode>;

type HandlerType = Handler<
    {
        address: BaseAddressModel;
        validationRules: CustomerValidationDto;
        addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    } & HasOnSuccess<AddressErrors>,
    {
        addressErrors: AddressErrors;
    }
>;

export const PiyoviValidateAddress: HandlerType = async props => {
    const customApplicationState = props.getState() as CustomApplicationState;
    const byPassAddressValidation = customApplicationState.customCheckoutShippingState.bypassPiyoviAddressValidation;
    const enablePiyoviAvs = piyoviSettingSelector(props.getState())?.enablePiyoviAvs;

    const { address1, address2, city, state, postalCode, country, companyName } =
        props.parameter.address;

    if (!enablePiyoviAvs) {
        return;
    }

    // Only run Piyovi on a real customer submit (Continue). This handler is injected into
    // the shared validateAddress chain, which also runs on page load / SSR via
    // SetInitialValues -> ValidateShippingAddress/ValidateBillingAddress against the saved
    // shipTo/billTo (stored as the form-state address, so reference match can't distinguish
    // it). CheckoutShippingPage.submitForm sets this flag around the Continue validators.
    if (!customApplicationState.customCheckoutShippingState.isCheckoutShippingSubmitting) {
        return;
    }

    // Which form (shipping vs billing) is being validated — the active form passes its own
    // address object straight through the validator chain, so a reference match tells us.
    const checkoutShipping = customApplicationState.pages?.checkoutShipping;
    let addressType: PiyoviAddressType | undefined;
    if (props.parameter.address === checkoutShipping?.shippingAddressFormState?.address) {
        addressType = "shipping";
    } else if (props.parameter.address === checkoutShipping?.billingAddressFormState?.address) {
        addressType = "billing";
    }

    // Bypass, once the customer has resolved an address's confirmation modal.
    // Guests use a per-address bypass so resolving one address does not suppress the other
    // (both billing + one-time shipping must be validated). Non-guests keep the single flag.
    if (getCurrentUserIsGuest(props.getState())) {
        if (addressType && customApplicationState.customCheckoutShippingState.bypassPiyoviByType[addressType]) {
            return;
        }
    } else if (byPassAddressValidation) {
        return;
    }

    if (
        !address1?.trim() ||
        !city?.trim() ||
        !state?.abbreviation?.trim() ||
        !postalCode?.trim() ||
        !country?.abbreviation?.trim()
    ) {
        return;
    }

    const isCountryUSorCA = country.abbreviation === "US" || country.abbreviation === "CA";
    if (!isCountryUSorCA) {
        return;
    }

    const addressValidationApiParameter: AddressValidationApiParameter = {
        companyName,
        address1,
        address2,
        city,
        stateProvince: state?.abbreviation,
        zipPostalCode: postalCode,
        country: country?.abbreviation,
    };

    const result = await getAddressValidationResult(addressValidationApiParameter);

    const enteredAddress: ValidatedAddressModel = {
        address1,
        address2,
        city,
        stateProvince: state?.abbreviation,
        zipPostalCode: postalCode,
        country: country?.abbreviation,
    };

    // Verified as entered → nothing to confirm, let checkout proceed.
    if (result.isSuccess && result.isSameAddress) {
        return;
    }

    // Piyovi returned a suggestion that only differs by case/whitespace/punctuation
    // → effectively the same address, nothing meaningful to confirm, let checkout proceed.
    if (
        result.isSuccess &&
        result.hasSuggestion &&
        result.validatedAddress &&
        areValidatedAddressesEquivalent(enteredAddress, result.validatedAddress)
    ) {
        return;
    }

    // Otherwise the customer must make a choice: confirm a suggestion (Mode A),
    // or acknowledge an unverifiable / technical failure (Mode B).
    props.addressErrors.isPiyoviValidationError = true;

    if (!result.hasSuggestion) {
        // Mode B — surface inline messaging alongside the modal.
        props.addressErrors.address1 = translate("Invalid address");

        result.errors?.forEach(errObj => {
            const err = errObj ?? "";
            const lower = err.toLowerCase();

            if (lower.includes("zip") || lower.includes("postal")) props.addressErrors.postalCode = err;
            if (lower.includes("city")) props.addressErrors.city = err;
            if (lower.includes("state")) props.addressErrors.state = err;
            if (lower.includes("address")) props.addressErrors.address1 = err;
        });
    }

    props.dispatch({
        type: "Components/CustomCheckoutShipping/SetPiyoviValidationResult",
        piyoviValidation: {
            enteredAddress,
            suggestedAddress: result.hasSuggestion ? result.validatedAddress : undefined,
            hasSuggestion: result.hasSuggestion,
            addressType,
        },
    });
};

addToChainAfter(chain, ValidatePostalCode, PiyoviValidateAddress);
